import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M19 L19.03 — Create a new deployment.
 *
 * Posts to `POST /v1/platform/deployments`. Environments are loaded from
 * `GET /v1/platform/environments`; if none load, the environment id can be
 * entered directly.
 */
interface EnvironmentRow {
  readonly environmentId: string;
  readonly environmentName: string;
  readonly environmentCode: string;
}

export function NewDeploymentPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [environments, setEnvironments] = useState<EnvironmentRow[]>([]);
  const [environmentId, setEnvironmentId] = useState('');
  const [releaseVersion, setReleaseVersion] = useState('');
  const [deploymentNotes, setDeploymentNotes] = useState('');
  const [rollbackPlan, setRollbackPlan] = useState('');
  const [approvalReference, setApprovalReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: EnvironmentRow[] }>('/v1/platform/environments', { token })
      .then((r) => { if (alive) setEnvironments(r.data ?? []); })
      // Entry falls back to free text, but the operator is told why the list is missing.
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!environmentId.trim() || !releaseVersion.trim()) {
      setError(t('m19.newDeployment.validationRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest('/v1/platform/deployments', {
        method: 'POST',
        token,
        body: {
          environmentId: environmentId.trim(),
          releaseVersion: releaseVersion.trim(),
          deploymentNotes: deploymentNotes.trim() || undefined,
          rollbackPlan: rollbackPlan.trim() || undefined,
          approvalReference: approvalReference.trim() || undefined,
        },
      });
      navigate('/admin/platform/deployments');
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <main aria-label="new-deployment">
      <h1 style={{ fontSize: 20, marginBottom: 16 }}>{t('m19.newDeployment.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      <form aria-label="new-deployment-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m19.newDeployment.labelEnvironment')}
          {environments.length > 0 ? (
            <select aria-label="environment" value={environmentId} onChange={(e) => setEnvironmentId(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
              <option value="">{t('m19.newDeployment.selectPlaceholder')}</option>
              {environments.map((env) => (
                <option key={env.environmentId} value={env.environmentId}>
                  {`${env.environmentName} (${env.environmentCode})`}
                </option>
              ))}
            </select>
          ) : (
            <input aria-label="environment" value={environmentId} onChange={(e) => setEnvironmentId(e.target.value)}
                   style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          )}
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m19.newDeployment.labelReleaseVersion')}
          <input aria-label="release-version" value={releaseVersion} onChange={(e) => setReleaseVersion(e.target.value)}
                 style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m19.newDeployment.labelDeploymentNotes')}
          <textarea aria-label="deployment-notes" value={deploymentNotes} onChange={(e) => setDeploymentNotes(e.target.value)}
                    rows={2} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m19.newDeployment.labelRollbackPlan')}
          <textarea aria-label="rollback-plan" value={rollbackPlan} onChange={(e) => setRollbackPlan(e.target.value)}
                    rows={2} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('m19.newDeployment.labelApprovalReference')}
          <input aria-label="approval-reference" value={approvalReference} onChange={(e) => setApprovalReference(e.target.value)}
                 style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </label>

        <button type="submit" data-testid="submit-deployment" disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid var(--color-brand-primary)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', alignSelf: 'flex-start' }}>
          {submitting ? t('m19.newDeployment.creating') : t('m19.newDeployment.create')}
        </button>
      </form>
    </main>
  );
}
